import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { SkipLink, MAIN_CONTENT_ID } from '../components/SkipLink.tsx';

type Status = 'working' | 'done';

/**
 * The landing spot for the unsubscribe link in every briefing email (T-069)
 * — reached cold, from whatever device an inbox happens to be open on, so
 * this route sits outside the auth-gated app layout entirely (see the
 * pathless layout route in `router.tsx`) and never asks for a login.
 *
 * The call fires the instant the token shows up in the query string and
 * always lands on the same one-sentence confirmation, whether the token was
 * real, already used, or missing — `unsubscribeByToken` behaves the same
 * way server-side, so this page can't be used to probe for a valid token
 * either. No "are you sure", no upsell: this is the one screen in the app
 * someone reaches while annoyed.
 */
export function UnsubscribeRoute() {
  const [status, setStatus] = useState<Status>('working');

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    (token ? api.unsubscribe(token) : Promise.resolve({}))
      .catch(() => {
        // Same confirmation either way — see the comment above.
      })
      .finally(() => setStatus('done'));
  }, []);

  return (
    <div className="shell">
      <SkipLink />
      <header className="masthead">
        <h1 className="wordmark">
          The <em>Gaffer</em>
        </h1>
      </header>
      <main id={MAIN_CONTENT_ID} tabIndex={-1}>
        <section className="panel" aria-live="polite">
          {status === 'working' ? (
            <p className="hint">Unsubscribing…</p>
          ) : (
            <p>You won't get any more deadline briefing emails.</p>
          )}
        </section>
      </main>
    </div>
  );
}
